/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `EmailOutlineProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface EmailOutlineProps {
    /**
     * A string representing the CSS class to be applied to the EmailOutlineIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#282828'
     */
    color1?: string;
    /**
     * A number or string representing the height of the EmailOutlineIcon element.
     *
     * @default 16
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'EmailOutline'
     */
    testId?: string;
    /**
     * A number or string representing the height of the EmailOutlineIcon element.
     *
     * @default 22
     */
    width?: number | string;
}

/**
 * The `EmailOutline` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `EmailOutlineProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the EmailOutlineIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the EmailOutlineIcon element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the EmailOutlineIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const EmailOutlineComponent = <EmailOutline className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const EmailOutline = ({
    className = '',
    color1 = '#282828',
    height = 16,
    ref,
    testId = 'EmailOutline',
    width = 22
}: EmailOutlineProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 22 16"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M19.15.1c1.2 0 2.15 1 2.15 2.2v10.64c0 1.2-.95 2.21-2.15 2.21H2.25a2.2 2.2 0 0 1-2.15-2.2V2.3C.1 1.1 1.05.1 2.25.1zm-7.04 9.87c-.79.74-2.02.74-2.8 0l-1.49-1.4-5.37 5.36h16.5L13.6 8.56zM1.32 12.94q0 .18.05.33l5.56-5.55-5.6-5.32zm13.16-5.23 5.55 5.55q.05-.14.05-.32V2.4zM2.25 1.32 2 1.36l8.14 7.72a.8.8 0 0 0 .56.25.8.8 0 0 0 .55-.25l8.13-7.72-.24-.04z" fill={color1} stroke={color1} strokeWidth=".2" />
    </svg>
);

EmailOutline.displayName = 'EmailOutline';